import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, Trash2, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortalSavedPage = () => {
  const navigate = useNavigate();
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasActivePass, setHasActivePass] = useState(false);

  useEffect(() => {
    fetchSavedItems();
    checkPassStatus();
  }, []);

  const fetchSavedItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/saved-items/my-saved?item_type=TALENT_PROFILE`);
      setSavedItems(response.data.saved_items || []);
    } catch (error) {
      console.error('Failed to fetch saved items:', error);
      toast.error('Failed to load saved items');
    } finally {
      setLoading(false);
    }
  };

  const checkPassStatus = async () => {
    try {
      const response = await axios.get(`${API}/portal/talent/pass-status`);
      setHasActivePass(response.data.has_active_pass);
    } catch (error) {
      console.error('Failed to check pass status:', error);
    }
  };

  const handleUnsave = async (itemId) => {
    try {
      await axios.delete(`${API}/saved-items/unsave?item_type=TALENT_PROFILE&item_id=${itemId}`);
      toast.success('Candidate removed from saved');
      fetchSavedItems();
    } catch (error) {
      toast.error('Failed to remove candidate');
    }
  };

  const renderTalentCard = (item) => {
    const talent = item.item_data;
    const user = talent?.user;
    
    if (!talent) return null;
    
    const canViewDetails = hasActivePass;
    
    return (
      <Card key={item.item_id} className="hover:shadow-lg transition-shadow">
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div 
              className="flex-1 cursor-pointer"
              onClick={() => {
                if (canViewDetails) {
                  navigate(`/portal/talent/${item.item_id}`);
                } else {
                  toast.error('Active Employer Pass required to view full details');
                }
              }}
            >
              <div className="flex items-start gap-2">
                <h3 className="text-lg font-semibold text-foreground">
                  {canViewDetails ? talent.display_name : '••••••••'}
                </h3>
                {!canViewDetails && <Lock className="w-4 h-4 text-muted-foreground mt-1" />}
              </div>
              
              <p className="text-sm text-muted-foreground">
                {talent.location_suburb}, {talent.location_state}
              </p>
              
              <div className="flex flex-wrap gap-2 mt-2">
                {talent.skills && talent.skills.slice(0, 3).map((skill, idx) => (
                  <Badge key={idx} variant="outline">{skill}</Badge>
                ))}
                {talent.skills && talent.skills.length > 3 && (
                  <Badge variant="outline">+{talent.skills.length - 3} more</Badge>
                )}
              </div>
              
              {canViewDetails && talent.experience_summary && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {talent.experience_summary}
                </p>
              )}
            </div>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleUnsave(item.item_id);
              }}
              className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
              title="Remove from saved"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
          
          <div className="flex justify-between items-center mt-3 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Saved: {new Date(item.saved_at).toLocaleDateString()}
            </p>
            
            {!canViewDetails && (
              <button
                onClick={() => navigate('/portal/talent/access')}
                className="text-xs px-3 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                Unlock Access
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
          <Heart className="w-8 h-8 text-red-500" />
          Saved Talent
        </h1>
        <p className="text-muted-foreground">Candidates you've bookmarked</p>
      </div>

      {!hasActivePass && (
        <Card className="mb-6 border-orange-200 bg-orange-50 dark:bg-orange-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-orange-800 dark:text-orange-200">
                  Limited Access
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                  You can save candidates, but an active Employer Pass is required to view full details and contact information.
                </p>
                <button
                  onClick={() => navigate('/portal/talent/access')}
                  className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                >
                  Activate Employer Pass
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading saved candidates...</p>
        </div>
      ) : savedItems.length === 0 ? (
        <div className="text-center py-20">
          <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-semibold text-foreground mb-2">No Saved Candidates</h2>
          <p className="text-muted-foreground mb-6">
            You haven't saved any talent profiles yet. Browse candidates and click the heart icon to save them for later.
          </p>
          <button
            onClick={() => navigate('/portal/talent')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Browse Talent
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {savedItems.map((item) => renderTalentCard(item))}
        </div>
      )}
    </div>
  );
};

export default PortalSavedPage;
